import { useCallback, useImperativeHandle, useLayoutEffect, useMemo, useRef, type Ref } from 'react';
import { Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';

import { BRAND } from '../shared/theme/paletteDark';
import { FONTS } from '../shared/theme/typography';
import Icon from './Icon';
import { TOGGLE_FILL, toggleMetrics, type ToggleIcon, type ToggleItem } from './viewToggleMetrics';

/**
 * One pill of N items: Layout (List | Calendar | Map), as icons on every
 * device since the Sept 23 afternoon chrome (contract §15). Words still work,
 * for any pill that wants them.
 *
 * Drawn from `viewToggleMetrics`: a pill with a parchment highlight inset from
 * its edges, and boxes tiled between those insets, each its label's width.
 *
 * The highlight is three pieces — two round caps and a middle — rather than one
 * box, because `scaleX` on a rounded box flattens its caps. Position and width
 * therefore move as transforms only, which keeps the move running on the
 * compositor while React renders the view behind it. Labels cross-fade between
 * regular parchment and bold black, since `font-weight` cannot animate; the
 * boxes are fixed, so nothing reflows underneath. Icons cross-fade the same way.
 *
 * It paints imperatively through `follow`, so that a tap moves it on the next
 * frame rather than after the view it switches to has rendered, and so a phone's
 * page swipe can drag the Scope pill along with the pages.
 *
 * Reduced motion is handled globally, in `fonts.css`: every transition here is
 * a CSS one, and that rule cuts its duration.
 */

/** The move, on every layout: the repo's `EASE_IN_OUT`, which the page swipe uses too. */
export const TOGGLE_MOVE_MS = 420;
export const TOGGLE_EASE = 'cubic-bezier(0.65, 0, 0.35, 1)';

export interface ViewToggleHandle {
  /** Paint at a float item index — 0 for the first item, 1 the second — over `ms`. */
  follow: (index: number, ms: number, ease: string) => void;
}

const Pill = styled('div')({
  position: 'relative',
  flexShrink: 0,
  borderRadius: 999,
  userSelect: 'none',
  WebkitTapHighlightColor: 'transparent',
});

/** Carries the highlight's position; the pieces inside carry its width. */
const Highlight = styled('div')({
  position: 'absolute',
  left: 0,
  pointerEvents: 'none',
  willChange: 'transform',
});

const Cap = styled('span')({
  position: 'absolute',
  top: 0,
  left: 0,
  borderRadius: 999,
  background: BRAND.parchment,
});

/**
 * Square-edged, stretched between the cap centres, so the caps keep their shape.
 *
 * It is drawn one highlight wide rather than one pixel, because the browser
 * snaps a box to whole device pixels before it scales it: a 1px bar at 175%
 * zoom on a 2x screen is painted 4 device pixels wide instead of 3.5, and
 * `scaleX` multiplies that 14% by the length, running the bar a dozen pixels
 * past the trailing cap and baking its square corners into the pill's edge.
 * A bar the height of the highlight leaves under half a pixel of that error.
 */
const Mid = styled('span')({
  position: 'absolute',
  top: 0,
  transformOrigin: 'left center',
  background: BRAND.parchment,
});

const Box = styled('button')({
  position: 'absolute',
  top: 0,
  bottom: 0,
  appearance: 'none',
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  borderRadius: 999,
  WebkitTapHighlightColor: 'transparent',
});

/**
 * Both labels sit in the same place and cross-fade. `lineHeight: 1` centred in
 * the box leaves the two baselines about a fifth of a pixel apart, which is
 * what "sharing the unselected label's baseline" comes to in a browser.
 */
const Label = styled('span')({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 1,
  fontFamily: FONTS.serif,
  whiteSpace: 'nowrap',
  pointerEvents: 'none',
});

const IconLayer = styled('span')({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'none',
});

/** The layout icons, Sam's artwork from `_Dev process` (`toggle - list view` …), one colour. */
const GLYPH: Record<ToggleIcon, string> = {
  list: 'ui:layout-list',
  calendar: 'ui:layout-calendar',
  map: 'ui:layout-map',
};

function Glyph({ icon, size }: { icon: ToggleIcon; size: number }) {
  return <Icon name={GLYPH[icon]} size={size} />;
}

interface Props<K extends string> {
  ref?: Ref<ViewToggleHandle>;
  /** What the group is, for a screen reader: "Scope", "Layout". */
  label: string;
  /** Which item is showing. */
  value: K;
  items: readonly ToggleItem<K>[];
  /** Icons instead of words, with the words as labels. */
  icons?: boolean;
  /** The words as tooltips over the icons: where there is a pointer to hover with. */
  tooltips?: boolean;
  /** A phone's pill sits on the page; every desktop width lets the page through. */
  fill?: keyof typeof TOGGLE_FILL;
  /** 1 everywhere but the phone header's Scope and the narrowest windows. */
  scale?: number;
  onSelect: (key: K) => void;
}

export default function ViewToggle<K extends string>({
  ref,
  label,
  value,
  items,
  icons = false,
  tooltips = false,
  fill = 'phone',
  scale = 1,
  onSelect,
}: Props<K>) {
  const m = useMemo(() => toggleMetrics(items, icons, scale), [items, icons, scale]);

  const nodes = useRef<{
    hl: HTMLDivElement | null;
    capR: HTMLSpanElement | null;
    mid: HTMLSpanElement | null;
    on: (HTMLElement | null)[];
    off: (HTMLElement | null)[];
  }>({ hl: null, capR: null, mid: null, on: [], off: [] });

  /** The index last painted, so a deferred `value` change does not restart the move. */
  const painted = useRef<number | null>(null);
  const paintedFor = useRef<string>('');

  const paint = useCallback(
    (index: number, ms: number, ease: string) => {
      const n = nodes.current;
      const last = m.boxes.length - 1;
      const at = Math.max(0, Math.min(last, index));
      const i = Math.min(Math.floor(at), last);
      const f = at - i;
      const a = m.boxes[i];
      const b = m.boxes[Math.min(i + 1, last)];
      const x = a.x + (b.x - a.x) * f;
      const w = a.w + (b.w - a.w) * f;
      const move = ms ? `transform ${ms}ms ${ease}` : 'none';
      const fade = ms ? `opacity ${ms}ms ${ease}` : 'none';

      if (n.hl) {
        n.hl.style.transition = move;
        n.hl.style.transform = `translateX(${x}px)`;
      }
      if (n.capR) {
        n.capR.style.transition = move;
        n.capR.style.transform = `translateX(${w - m.highlight}px)`;
      }
      if (n.mid) {
        n.mid.style.transition = move;
        n.mid.style.transform = `scaleX(${Math.max(0, w - m.highlight) / m.highlight})`;
      }
      for (let k = 0; k <= last; k += 1) {
        const on = k === i ? 1 - f : k === i + 1 ? f : 0;
        const onEl = n.on[k];
        const offEl = n.off[k];
        if (onEl) {
          onEl.style.transition = fade;
          onEl.style.opacity = String(on);
        }
        if (offEl) {
          offEl.style.transition = fade;
          offEl.style.opacity = String(1 - on);
        }
      }
      painted.current = at;
    },
    [m],
  );

  useImperativeHandle(ref, () => ({ follow: paint }), [paint]);

  // A tap paints before `value` catches up, so only a change it has not already
  // played — a browser's back button, say — moves it here.
  useLayoutEffect(() => {
    const found = m.boxes.findIndex((b) => b.key === value);
    const target = found >= 0 ? found : Math.min(painted.current ?? 0, m.boxes.length - 1);
    const key = `${m.boxes.map((b) => b.key).join()}:${icons}:${scale}`;
    const jump = painted.current === null || paintedFor.current !== key;
    paintedFor.current = key;
    if (!jump && painted.current === target) return;
    paint(target, jump ? 0 : TOGGLE_MOVE_MS, TOGGLE_EASE);
  }, [value, icons, scale, m, paint]);

  /** Arrow keys walk the radios, as a radio group's do. */
  const onKeyDown = (index: number) => (e: React.KeyboardEvent) => {
    const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
    if (!step) return;
    e.preventDefault();
    const next = (index + step + m.boxes.length) % m.boxes.length;
    onSelect(items[next].key);
  };

  return (
    <Pill
      role="radiogroup"
      aria-label={label}
      style={{ width: m.width, height: m.height, background: TOGGLE_FILL[fill] }}
    >
      <Highlight
        ref={(el) => {
          nodes.current.hl = el;
        }}
        aria-hidden="true"
        style={{ top: m.inset, height: m.highlight }}
      >
        <Cap style={{ width: m.highlight, height: m.highlight }} />
        <Cap
          ref={(el) => {
            nodes.current.capR = el;
          }}
          style={{ width: m.highlight, height: m.highlight }}
        />
        <Mid
          ref={(el) => {
            nodes.current.mid = el;
          }}
          style={{ left: m.highlight / 2, width: m.highlight, height: m.highlight }}
        />
      </Highlight>

      {m.boxes.map((box, i) => {
        const item = items[i];
        const glyph = icons && item.icon;
        const button = (
          <Box
            key={box.key}
            type="button"
            role="radio"
            aria-checked={value === box.key}
            // Always named: the words are drawn twice for the cross-fade, and read once.
            aria-label={item.label}
            tabIndex={value === box.key ? 0 : -1}
            onKeyDown={onKeyDown(i)}
            onClick={() => onSelect(item.key)}
            style={{ left: box.x, width: box.w }}
          >
            {glyph ? (
              <>
                <IconLayer
                  ref={(el) => {
                    nodes.current.off[i] = el;
                  }}
                  style={{ color: BRAND.parchment }}
                >
                  <Glyph icon={item.icon!} size={m.icon} />
                </IconLayer>
                <IconLayer
                  ref={(el) => {
                    nodes.current.on[i] = el;
                  }}
                  style={{ color: '#000' }}
                >
                  <Glyph icon={item.icon!} size={m.icon} />
                </IconLayer>
              </>
            ) : (
              <>
                <Label
                  ref={(el) => {
                    nodes.current.off[i] = el;
                  }}
                  style={{ fontSize: m.labelOff, fontWeight: 400, color: BRAND.parchment }}
                >
                  {item.label}
                </Label>
                <Label
                  ref={(el) => {
                    nodes.current.on[i] = el;
                  }}
                  style={{ fontSize: m.labelOn, fontWeight: 700, color: '#000' }}
                >
                  {item.label}
                </Label>
              </>
            )}
          </Box>
        );
        return glyph && tooltips ? (
          <Tooltip key={box.key} title={item.label} placement="bottom" enterDelay={400} disableInteractive>
            {button}
          </Tooltip>
        ) : (
          button
        );
      })}
    </Pill>
  );
}
